"use client";
import React from "react";
import Image from "next/image";
import genAiImage from "@/assets/images/genAi/gen-ai.svg";
import genAiBgCardImage from "@/assets/images/genAi/bg-cards-cover.svg";
import CommonSubtitle from "@/components/common/CommonSubtitle";
import Lottie from "lottie-react";
import genAiImageVideoJson from "@/assets/images/genAi/gen-ai.json";
import CommonTitle from "@/components/common/CommonTitle";

interface MetricCardProps {
  value: string;
  label: string;
}

const MetricCard: React.FC<MetricCardProps> = ({ value, label }) => {
  return (
    <div
      className="relative overflow-hidden rounded-2xl bg-[#F0F0FF] p-3 text-center shadow-sm"
      style={{
        backgroundImage: `url(${genAiBgCardImage.src})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="relative z-10">
        <div className="text-[22px] font-bold leading-[36px] text-[#000000] xl:text-[28px]">
          {value}
        </div>
        <div className="text-[18px] font-medium text-[#000000] xl:text-[20px]">
          {label}
        </div>
      </div>
    </div>
  );
};

const GenAiFeatures = () => {
  const metricsData = [
    {
      value: "5+",
      label: "Leading LLM Models",
    },
    {
      value: "99.9%",
      label: "AI Uptime",
    },
    {
      value: "10x",
      label: "Faster Processing",
    },
  ];

  return (
    <div className="bg-white py-12 xl:py-20">
      <div className="container mx-auto px-4">
        <div className="mb-16">
          <CommonTitle
            color="#202020"
            fontSizeClass="text-[24px] lg:text-[32px] mb-2 lg:mb-4 text-center"
            fontWeightClass="font-semibold"
            as="h2"
          >
            <span className="bg-gradient-to-r from-[#781E8F] to-[#422CBB] bg-clip-text text-transparent">
              GenAI
            </span>{" "}
            isn&apos;t an add-on; it&apos;s our core
          </CommonTitle>
          <CommonSubtitle
            color="#202020"
            fontSizeWeight="max-w-[600px] mx-auto text-center"
          >
            Kroolo&apos;s supercharged AI is built into every pixel. Experience
            the power of multiple LLMs orchestrated intelligently across all
            your workflows.
          </CommonSubtitle>
        </div>
        <div className="mb-16 flex justify-center" aria-hidden="true">
          <div className="relative w-full">
            {genAiImage ? (
              <Lottie
                animationData={genAiImageVideoJson}
                alt="Kroolo GenAI - Multi-LLM orchestration diagram"
                className="h-full w-full object-contain"
                width={500}
                height={500}
                loop
                autoplay
              />
            ) : (
              <Image
                src={genAiImage}
                alt="Kroolo GenAI - Multi-LLM orchestration diagram"
                className="h-full w-full object-contain"
                width={500}
                height={500}
              />
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
          {metricsData.map((metric, index) => (
            <MetricCard key={index} value={metric.value} label={metric.label} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default GenAiFeatures;
